import React, { memo } from 'react';
import classNames from 'classnames';

import { isDeepChanged } from '../../../utils/ChangeSpy';
import { noop } from '../../../utils/noop';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import If from '../../helpers/If';
import CompanyMarkets from '../Details/CompanyMarkets';
import CompanyProducts from '../Details/CompanyProducts';
import CompanyCustomers from '../Details/CompanyCustomers';
import CompanyCompetitors from '../Details/CompanyCompetitors';
import CompanyFacilities from '../Details/CompanyFacilities';
import CompanyTradeShows from '../Details/CompanyTradeShows';
import { Broad as BaseBroad } from '../Details/Broad';
import CompanyCustomFields from '../Details/CompanyCustomFields';
import CompanyHarvcoTags from '../Details/CompanyHarvcoTags';
import { createIndustryErrorMessage, INDUSTRY_CATEGORIES } from '../../../utils/industryTagsHelper';

import style from './style.module.css';

const Broad = PrettyError(DisplayError(BaseBroad));

/**
 * Stateless component for contact's activities.
 * Display table where rows are activities.
 *
 * @link http://screencloud.net/v/jS6q on page (right top part)
 * @link http://screencloud.net/v/7yc2 component itself
 * @param props {Object}.
 */
const CompanyDetailsComponent = ({
  hasPermissions,
  canEditData,
  details,
  suggestCreate,
  onAdd = noop,
  onClick,
  onChange,
  onDelete,
  onTradeShowDelete,
  industryCategories,
  isDuplicateCompany,
  businessModels,
  onUpdateTags,
  error,
  onErrorClose,
}) => {
  const callbacks = { onAdd, onClick, onChange, onDelete };
  const headerClass = classNames('panel-title', {
    'pl5 changed': isDeepChanged(details) || isDeepChanged(industryCategories),
  });

  const errorMessage = createIndustryErrorMessage(error);

  return (
    <div className="panel panel-default">
      <div className="panel-heading" data-parent="#accordion" data-target="#acc-slide-2" data-toggle="collapse">
        <h4 className={headerClass}>Company Details</h4>
      </div>
      <div className="panel-collapse collapse in" id="acc-slide-2">
        <div className={classNames(style.body, 'panel-body')}>
          <div className={style.details}>
            <CompanyMarkets
              {...callbacks}
              canEditData={canEditData}
              data={details.get('targetMarkets')}
              disabled={isDuplicateCompany}
              extraClassName={classNames(style.table, style.smallTable)}
            />
            <CompanyProducts
              {...callbacks}
              canEditData={canEditData}
              data={details.get('targetProducts')}
              disabled={isDuplicateCompany}
              extraClassName={classNames(style.table, style.smallTable)}
            />
            <CompanyCustomers
              {...callbacks}
              canEditData={canEditData}
              data={details.get('targetCustomers')}
              disabled={isDuplicateCompany}
              extraClassName={classNames(style.table, style.smallTable)}
            />
            <If value={hasPermissions}>
              <CompanyCompetitors
                {...callbacks}
                canEditData={canEditData}
                data={details.get('targetCompetitors')}
                disabled={isDuplicateCompany}
                extraClassName={classNames(style.table, style.smallTable)}
              />
            </If>
            <CompanyFacilities
              {...callbacks}
              canEditData={canEditData}
              data={details.get('targetFacilities')}
              disabled={isDuplicateCompany}
              extraClassName={style.table}
            />
            <CompanyCustomFields
              {...callbacks}
              canEditData={canEditData}
              data={details.get('customFields')}
              disabled={isDuplicateCompany}
              extraClassName={style.table}
            />
            <div className={classNames(style.table, style.broad, 'table-responsive mb4')}>
              <Broad
                {...callbacks}
                businessModels={businessModels}
                canEditData={canEditData}
                data={industryCategories}
                deprecatedIndustries={details.get('industries')}
                disabled={isDuplicateCompany}
                error={errorMessage}
                name="industryCategories"
                onClick={onUpdateTags}
                onErrorClose={event => onErrorClose(event, INDUSTRY_CATEGORIES)}
              />
            </div>
            <CompanyTradeShows
              canEditData={canEditData}
              data={details.get('tradeShowTargets')}
              disabled={isDuplicateCompany}
              extraClassName={style.table}
              onAdd={onAdd}
              onClick={onClick}
              onDelete={onTradeShowDelete}
              suggestCreate={suggestCreate}
            />
            <CompanyHarvcoTags
              {...callbacks}
              canEditData={canEditData}
              data={details.get('harvcoTags')}
              disabled={isDuplicateCompany}
              extraClassName={style.table}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Company details component. */
export const CompanyDetails = memo(CompanyDetailsComponent);
